// ═══════════════════════════════════════════════════════════════════════════
// grainPosition.js — GrainPosition, Stage 1 of the Silas Action Framework
// (ROADMAP.md §1.2). Exposure + current protection ONLY. No break-even, no
// market context, no recommendation — those are Stages 2-6 and are left null
// by makePosition() on purpose.
//
//   exposure           = grainBushelsFromCoverage() over this farm's crop_coverage
//                         rows for (crop, cropYear) — RMA's liability-first
//                         guarantee, PP-adjusted, SCO/ECO/silage excluded.
//   currentProtection   = grain_contracts rows for the same (crop, cropYear),
//                         with the FOUR traps in tools/position-fixture.json
//                         §grain_contracts handled explicitly (see below).
//
// PURE — takes already-fetched rows, returns a Position object. Fetching is a
// caller concern (this stage does not add a new sbFetch call site).
// ═══════════════════════════════════════════════════════════════════════════

import { grainBushelsFromCoverage, grainChopAdjust } from "./grainRules";
import { makePosition, TIER } from "./shared";
import { summarizeGrainCommitment, pctOfExpected } from "./grainCommitment";

// Same crop-name collapse grainBushelsFromCoverage uses internally (WHEAT/
// WINTER WHEAT are one crop), applied here so grain_contracts rows — which
// carry mixed-case crop names like "Corn"/"Soybeans" in the live book,
// verified 2026-08-16 (see tools/position-fixture.json) — match the
// crop_coverage side, which is upper-case.
export function cropMatches(rowCrop, targetCrop) {
  const u = String(rowCrop || "").toUpperCase();
  const t = String(targetCrop || "").toUpperCase();
  return t === "WHEAT" ? (u === "WHEAT" || u === "WINTER WHEAT") : u === t;
}

// ── grain_contracts, filtered and summed per the FOUR traps measured
// 2026-08-16 (tools/position-fixture.json §grain_contracts._traps) ──────────
//
//   1. `filled` is FALSE on all 460 rows — dead column, NEVER READ here.
//   2. 112 rows (24%) carry bushels_contracted = 0 (or null). They sum to
//      nothing but inflate a naive COUNT — excluded from `contractCount`,
//      counted separately as `zeroBushelRows` so the exclusion is visible
//      rather than silent.
//   3. `bushels_unpriced` is NULL on 47% of rows — NEVER READ; unpriced
//      exposure is DERIVED as contracted − priced, per row, summed.
//   4. `cash_price` is NULL on real HTA/basis-open contracts — those bushels
//      count as CONTRACTED (and as unpriced, since bushels_priced is null on
//      that shape too) but must NOT enter the weighted average cash price
//      denominator or numerator. Weighted by bushels_priced (the bushels
//      actually locked at that row's cash_price), not bushels_contracted —
//      a partially-priced contract would otherwise overweight its own price.
// ── HOW MANY OF A ROW'S BUSHELS ARE ACTUALLY CASH-PRICED ───────────────────
// Returns the number the SOURCE FILE STATES, or null for "not stated" — never a
// guess. The caller applies its own fallback on null, so an absent answer and a
// zero answer stay different things.
//
// WHY THIS IS ONE RULE AND NOT TWO: the contract table and GrainPosition read
// `bushels_priced`, while GrainMarketingView's pricedBushels()/avgCashSold()
// decided a Basis contract was priced from `cash_price > 0` alone and then
// counted its WHOLE contracted quantity. Those disagree on the same row.
// W Cleveland's 104964-P00 is the measured example (2026-08-21): contract_type
// Basis, quantity priced 0, unpriced 113.21 bu, cash_price $5.29 — the elevator
// publishes a cash BID against a basis contract whose futures leg is still open,
// so the $5.29 is a quote, not money. The contract table called those bushels
// unpriced and the marketing card called them sold.
//
// NOT the same case as the priced-basis reclassification in parseGrainCSV (sql/039
// `futures_derived`): that one converts a basis line that IS priced, and is
// guarded by `quantityPriced > 0`. This is the complement — priced quantity ZERO.
//
// TWO PROVENANCE RULES, both measured over the live book, both load-bearing:
//
//   1. ONLY AN IMPORTED ROW'S `bushels_priced` IS THE ELEVATOR'S OWN "Quantity
//      Priced" COLUMN. Hand-entered rows carry whatever was typed, and two of the
//      six manual rows in the book are unusable: one Silsby Fixed Price row has
//      bushels_priced = 11.87 against 5,000 contracted — a soybean PRICE typed
//      into a bushels field. Trusting it would report 5,000 sold bushels as 11.87.
//      Provenance, never a ratio.
//   2. HTA IS EXCLUDED ON PURPOSE. An HTA locks FUTURES and leaves basis floating,
//      so the elevator reports quantity-priced 0 (nothing is cash-priced yet) while
//      the tool correctly treats the bushels as committed — 78,000 bu across 15
//      CY2026/27 rows. estimatedBasisBushels() is what labels that half as an
//      estimate. Do not "unify" HTA into this rule.
export function contractPricedBushels(row) {
  if (!row) return null;
  if (String(row.source || "").toLowerCase() !== "csv_import") return null;
  const t = String(row.contract_type || row.type || "").toUpperCase();
  if (t === "HTA") return null;
  const v = row.bushels_priced;
  if (v == null || !Number.isFinite(Number(v))) return null;   // never captured
  return Math.max(0, Number(v));
}
export function summarizeGrainContracts(contractRows, crop, cropYear) {
  const rows = (contractRows || []).filter(r =>
    Number(r.crop_year) === Number(cropYear) && cropMatches(r.crop, crop));

  let contractedBu = 0, pricedBu = 0, deliveredBu = 0;
  let priceWeightSum = 0, priceWeightBu = 0;
  let contractCount = 0, zeroBushelRows = 0, openPriceRows = 0;

  for (const r of rows) {
    const contracted = Number(r.bushels_contracted) || 0;
    if (!(contracted > 0)) { zeroBushelRows++; continue; }   // trap 2
    contractCount++;
    contractedBu += contracted;
    const priced = r.bushels_priced != null ? Number(r.bushels_priced) || 0 : 0;
    pricedBu += priced;
    deliveredBu += Number(r.bushels_delivered) || 0;
    const cashPrice = r.cash_price != null ? Number(r.cash_price) : null;
    if (cashPrice != null && cashPrice > 0) {
      // Weight by the bushels actually priced at this row's price. Where the FILE
      // states the priced quantity (contractPricedBushels above) that number is the
      // weight even when it is ZERO — a basis contract carrying an elevator cash BID
      // with nothing priced against it must contribute no price at all. The old
      // `priced > 0 ? priced : contracted` contradicted the comment it sat under and
      // pulled W Cleveland's 113.21 unpriced bushels into the average at $5.29.
      // Only where the file says nothing does contracted stand in.
      const stated = contractPricedBushels(r);
      const w = stated != null ? stated : (priced > 0 ? priced : contracted);
      if (!(w > 0)) { openPriceRows++; continue; }
      priceWeightSum += cashPrice * w;
      priceWeightBu += w;
    } else {
      openPriceRows++;                                        // trap 4
    }
  }

  const unpricedBu = Math.max(0, contractedBu - pricedBu);     // trap 3 — derived, never read
  return {
    contractedBu: rows.length ? contractedBu : null,
    pricedBu: rows.length ? pricedBu : null,
    unpricedBu: rows.length ? unpricedBu : null,
    deliveredBu: rows.length ? deliveredBu : null,
    avgPricedCashPrice: priceWeightBu > 0 ? priceWeightSum / priceWeightBu : null,
    contractCount,
    zeroBushelRows,
    openPriceRows,
    overDelivered: deliveredBu > contractedBu,
  };
}

// ── GrainPosition ─────────────────────────────────────────────────────────
// coverageRows: this farm's crop_coverage rows for cropYear (any crop; the
//   crop filter happens inside grainBushelsFromCoverage).
// unitsByKey: optional crop_coverage_units map (cuKey -> unit rows) for the
//   exact per-unit PP split. Omit and the PP-factor model is used instead —
//   both paths are the SAME plantedSplit() rule grainRules.js duplicates.
// contractRows: this farm's grain_contracts rows (any crop_year; filtered
//   internally to cropYear).
// choppedAcres: the grower's estimate of acres insured as GRAIN that will be
//   chopped for silage (grain_crop_plan.chopped_acres). 0 — the default, and the
//   state of every farm that does not chop — leaves the exposure exactly as the
//   policy states it. Non-zero haircuts the SELL CEILING and nothing else; see
//   grainChopAdjust in grainRules.js for why the policy itself does not move.
export function buildGrainPosition({ farm, crop, cropYear, coverageRows, unitsByKey = null, contractRows = [], choppedAcres = 0 }) {
  const exp = grainChopAdjust(grainBushelsFromCoverage(coverageRows, crop, cropYear, unitsByKey), choppedAcres);
  const protection = summarizeGrainContracts(contractRows, crop, cropYear);
  // commitment — PHYSICAL COMMITMENT vs PRICE PROTECTION, sourced from
  // contract_type (Kevin, 2026-08-19: "Do not display contracted/priced as
  // interchangeable"). Same crop_year/crop/zero-bushel-row filter as
  // `protection` above, so the two always agree on which rows count.
  const commitment = summarizeGrainCommitment(contractRows, crop, cropYear);

  const warnings = [];
  if (!exp) {
    warnings.push(`no priceable ${crop} coverage found for CY${cropYear} — exposure unknown`);
  } else if (exp.incomplete.length) {
    for (const inc of exp.incomplete) warnings.push(`${crop} ${inc.county}: ${inc.why}`);
  }
  // A ceiling that reads short with no reason attached is indistinguishable from
  // a dropped coverage line — the exact confusion `incomplete` above exists to
  // prevent — so the haircut announces itself wherever warnings are surfaced.
  if (exp && exp.chop && exp.chop.chopped > 0) {
    warnings.push(`${crop} CY${cropYear}: ${exp.chop.chopped.toLocaleString()} ac insured as grain are planned for chopping — the marketing guarantee is ${Math.round(exp.chop.share * 100)}% of the policy's, which still covers all the acres`);
    if (exp.chop.capped) {
      warnings.push(`${crop} CY${cropYear}: chopped acres entered (${exp.chop.asked.toLocaleString()}) exceed the acres insured as grain — capped, so the marketing guarantee is zero`);
    }
  }
  if (protection.zeroBushelRows > 0) {
    warnings.push(`${protection.zeroBushelRows} ${crop} CY${cropYear} contract row(s) carry zero bushels — excluded from counts`);
  }
  if (protection.openPriceRows > 0) {
    warnings.push(`${protection.openPriceRows} ${crop} CY${cropYear} contract(s) have committed bushels with no locked price (HTA/basis-open) — excluded from the weighted average price`);
  }
  if (protection.overDelivered) {
    warnings.push(`delivered bushels exceed contracted bushels for ${crop} CY${cropYear} — grain_contracts.filled is not a reliable signal here (see position-fixture.json)`);
  }

  // expectedBu (RMA's guarantee with the coverage-level haircut removed) is
  // the exposure denominator a marketing decision compares contracts against
  // — the guarantee itself (guaranteeBu) is the pre-harvest forward-sell
  // CEILING (CLAUDE.md), not the expected crop.
  const expectedBu = exp ? exp.expectedBu : null;
  const contractedBu = protection.contractedBu;
  const remainingBu = (expectedBu != null && contractedBu != null) ? expectedBu - contractedBu : null;
  // NOT clamped at 100 (ROADMAP.md PHASE II acceptance criteria, reused here
  // because over-commitment is real exposure information from day one, not
  // an economics judgment) — a farm at 130% contracted against expected yield
  // is a finding, not a display bug.
  const pctContracted = (expectedBu != null && expectedBu > 0 && contractedBu != null)
    ? +(contractedBu / expectedBu * 100).toFixed(1) : null;

  return makePosition({
    farm, commodity: crop, cropYear,
    exposure: exp ? {
      expectedBu: exp.expectedBu,
      guaranteeBu: exp.guaranteeBu,
      acres: exp.acres,
      liability: exp.liability,
      aph: exp.aph,
      coverageLevel: exp.coverage,
      ppAcres: exp.ppAcres,
      ppExact: exp.ppExact,
      incomplete: exp.incomplete,
      // What the chopped-acre estimate removed, so a shortened ceiling can name
      // its own reason. `chopped: 0` on every farm that does not chop.
      chop: exp.chop,
    } : null,
    currentProtection: {
      contractedBu,
      pricedBu: protection.pricedBu,
      unpricedBu: protection.unpricedBu,
      deliveredBu: protection.deliveredBu,
      avgPricedCashPrice: protection.avgPricedCashPrice,
      contractCount: protection.contractCount,
      remainingBu,
      pctContracted,
      // commitment — PHYSICAL vs PRICE-PROTECTION split (grainCommitment.js).
      // priceProtectedBu closes futures-direction risk (cash-priced + HTA's
      // locked-futures leg); basisProtectedBu (Basis contracts' locked-basis/
      // open-futures leg) and physicalOnlyBu (Bushels Only — nothing locked)
      // are kept separate, never folded in. pctPriceProtected is the ONE
      // number grainTargetPosition.js anchors its range on.
      commitment: {
        physicalBu: commitment.physicalBu,
        cashPricedBu: commitment.cashPricedBu,
        futuresProtectedBu: commitment.futuresProtectedBu,
        basisProtectedBu: commitment.basisProtectedBu,
        physicalOnlyBu: commitment.physicalOnlyBu,
        unknownProtectionBu: commitment.unknownProtectionBu,
        priceProtectedBu: commitment.priceProtectedBu,
        byContractType: commitment.byContractType,
        pctPriceProtected: pctOfExpected(commitment.priceProtectedBu, expectedBu),
        pctBasisProtected: pctOfExpected(commitment.basisProtectedBu, expectedBu),
        pctPhysicalOnly: pctOfExpected(commitment.physicalOnlyBu, expectedBu),
      },
    },
    // exposure comes straight off RMA's own liability (tier 2 — farm-specific
    // observed, the AIP's own file); protection comes off this farm's own
    // grain_contracts rows (tier 2). Neither leg is a benchmark or estimate.
    // Unknown poisons the tier per shared.js weakestTier — see below.
    tier: exp ? TIER.FARM_OBSERVED : TIER.UNKNOWN,
    warnings,
  });
}

// ── positionBannerState ──────────────────────────────────────────────────
// Kevin, 2026-08-18: "do not ask Kevin or the farmer to manually provide a
// grain position just so the advisor works... if position is not known, show
// a small POSITION NOT YET KNOWN state and let the market visuals become the
// hero. Unknown contract completeness must NOT become 0% sold."
//
// Summarizes a GrainPosition into exactly one of three states a banner can
// render honestly, off VERIFIED data only (crop_coverage exposure +
// grain_contracts protection) — never a fabricated percentage:
//   known   — exposure AND contract data both resolved.
//   partial — one side resolved, the other genuinely ABSENT from Silas's
//             data (not zero — grainRules.js's own unknown-vs-zero
//             convention: summarizeGrainContracts returns contractedBu=null,
//             never 0, when there are zero grain_contracts rows on file).
//   unknown — neither side resolved. FIXED 2026-08-18: buildGrainAction()
//             used to short-circuit to a no-market/no-cash stub whenever
//             exposure was null; it now still resolves market/cash/basis
//             context (action.hasExposure=false marks the difference from
//             every other data-quality state) — only bushel sizing, the
//             before/after scenario, and yield-risk tiers stay withheld.
//             This state only says the BANNER has nothing to show. The
//             forward curve (grainCurve.js) is fetched independently of
//             position/action either way, so App.jsx can show real board
//             context beside an "unknown" banner regardless.
export function positionBannerState(position) {
  const hasExposure = !!(position && position.exposure);
  const contractedKnown = !!(position && position.currentProtection && position.currentProtection.contractedBu != null);

  if (hasExposure && contractedKnown) {
    const ex = position.exposure, cp = position.currentProtection;
    return {
      state: "known",
      expectedBu: ex.expectedBu,
      contractedBu: cp.contractedBu,
      pricedBu: cp.pricedBu,
      pctContracted: cp.pctContracted,
      pctPriced: (ex.expectedBu > 0 && cp.pricedBu != null) ? +(cp.pricedBu / ex.expectedBu * 100).toFixed(1) : null,
    };
  }
  if (hasExposure && !contractedKnown) {
    return {
      state: "partial",
      expectedBu: position.exposure.expectedBu,
      contractedBu: null, pricedBu: null, pctContracted: null, pctPriced: null,
      reason: "expected production is known from RMA coverage; no grain_contracts record on file — contracted/priced % is unknown, not zero",
    };
  }
  if (!hasExposure && contractedKnown) {
    return {
      state: "partial",
      expectedBu: null,
      contractedBu: position.currentProtection.contractedBu,
      pricedBu: position.currentProtection.pricedBu,
      pctContracted: null, pctPriced: null,
      reason: "contract records exist but no priceable RMA coverage was found for this crop/year — expected production is unknown",
    };
  }
  return {
    state: "unknown",
    expectedBu: null, contractedBu: null, pricedBu: null, pctContracted: null, pctPriced: null,
    reason: "no exposure or contract data resolved for this farm/crop/year — market intelligence is still available",
  };
}
